import { Alert, Badge, Button, Group, Loader, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconRotate } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getKnowledgeLoopRun,
  redoKnowledgeLoopRun,
  type KnowledgeRunDetail,
} from '@mygpt/app/apis/services/knowledgeLoop';
import {
  CARD_BORDER,
  SIGNALS,
  errorDetail,
  formatDuration,
  formatJst,
  formatTokens,
  proposalColor,
  runStatusColor,
  unitStatusColor,
} from './shared';

interface Props {
  datasetId: string;
  runId: string | null;
  onClose: () => void;
  onRedo?: () => void;
}

const Cell = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack gap={0}>
    <Text size="xs" c="gray.6">
      {label}
    </Text>
    <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Text>
  </Stack>
);

/** 1 回の蒸留の内訳：入力・決定の数、スキップされた証拠と理由、この回に出た候補 */
export const RunDetailModal = ({ datasetId, runId, onClose, onRedo }: Props) => {
  const { t } = useTranslation();
  const [run, setRun] = useState<KnowledgeRunDetail | null>(null);
  const [redoConfirm, setRedoConfirm] = useState(false);
  const [redoing, setRedoing] = useState(false);

  // この回をやり直す：未審査の候補を消して原料を戻し、もう一度抽出（品質ゲートを変えた後の再検証用）
  const redo = async () => {
    if (!runId) return;
    setRedoing(true);
    try {
      await redoKnowledgeLoopRun(datasetId, runId);
      showNotification({ color: 'green', message: t('knowledge_loop.run_detail.redo_done') });
      setRedoConfirm(false);
      onClose();
      onRedo?.();
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.distill.run_error') });
    } finally {
      setRedoing(false);
    }
  };

  useEffect(() => {
    setRun(null);
    setRedoConfirm(false);
    if (!runId) return;
    let alive = true;
    getKnowledgeLoopRun(datasetId, runId)
      .then(({ data }) => alive && setRun(data))
      .catch((e) => console.error('load run failed:', e));
    return () => {
      alive = false;
    };
  }, [datasetId, runId]);

  const s = run?.stats || {};
  return (
    <Modal
      opened={!!runId}
      onClose={onClose}
      size="lg"
      centered
      title={
        <Group gap={8}>
          <Text fw={600}>{t('knowledge_loop.run_detail.title')}</Text>
          {run && (
            <Badge size="sm" variant="light" color={runStatusColor(run.status)}>
              {t(`knowledge_loop.run_status.${run.status}`)}
            </Badge>
          )}
        </Group>
      }
    >
      {!run ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack gap="md">
          <Text size="xs" c="gray.6">
            {formatJst(run.started_at)} JST ・ {t(`knowledge_loop.trigger.${run.trigger}`)} ・{' '}
            {formatDuration(s.duration_sec)} ・ {formatTokens(s.tokens)} tokens
          </Text>
          {run.error && (
            <Alert color="red" variant="light">
              <Text size="xs" style={{ wordBreak: 'break-all' }}>
                {run.error}
              </Text>
            </Alert>
          )}
          <SimpleGrid cols={4} spacing="sm">
            {SIGNALS.map((sig) => (
              <Cell key={sig} label={t(`knowledge_loop.signal.${sig}`)} value={s.inputs?.[sig] || 0} />
            ))}
            <Cell label={t('knowledge_loop.run_detail.candidates')} value={s.candidates || 0} />
            <Cell label={t('knowledge_loop.proposal.add')} value={s.add || 0} />
            <Cell label={t('knowledge_loop.proposal.update')} value={s.update || 0} />
            <Cell label={t('knowledge_loop.proposal.conflict')} value={s.conflict || 0} />
            <Cell label={t('knowledge_loop.run_detail.noop')} value={s.noop || 0} />
            <Cell label={t('knowledge_loop.run_detail.skipped')} value={s.skipped || 0} />
            <Cell label={t('knowledge_loop.run_detail.llm_failed')} value={s.llm_failed || 0} />
            <Cell label={t('knowledge_loop.run_detail.deferred')} value={s.deferred || 0} />
            <Cell label={t('knowledge_loop.run_detail.duplicate_target')} value={s.duplicate_target || 0} />
            <Cell label={t('knowledge_loop.run_detail.quality_rejected')} value={s.quality_rejected || 0} />
            <Cell label={t('knowledge_loop.run_detail.same_as_merged')} value={s.same_as_merged || 0} />
            <Cell label={t('knowledge_loop.run_detail.wording_noop')} value={s.wording_noop || 0} />
            <Cell label={t('knowledge_loop.run_detail.contradiction_conflict')} value={s.contradiction_conflict || 0} />
            <Cell label={t('knowledge_loop.run_detail.retried')} value={s.retried || 0} />
            <Cell label={t('knowledge_loop.run_detail.merge_retried')} value={s.merge_retried || 0} />
            <Cell label={t('knowledge_loop.run_detail.as_is_skipped')} value={s.as_is_skipped || 0} />
            <Cell label={t('knowledge_loop.run_detail.pii_masked')} value={s.pii_masked || 0} />
          </SimpleGrid>
          {(!!s.demand || !!s.faq_synced) && (
            <SimpleGrid cols={4} spacing="sm">
              <Cell label={t('knowledge_loop.run_detail.demand_linked_unit')} value={s.demand?.linked_unit || 0} />
              <Cell
                label={t('knowledge_loop.run_detail.demand_linked_candidate')}
                value={s.demand?.linked_candidate || 0}
              />
              <Cell label={t('knowledge_loop.run_detail.demand_gap_open')} value={s.demand?.gap_open || 0} />
              <Cell label={t('knowledge_loop.run_detail.faq_synced')} value={s.faq_synced || 0} />
            </SimpleGrid>
          )}
          {!!s.budget_exhausted && (
            <Alert color="yellow" variant="light">
              <Text size="xs">{t('knowledge_loop.run_detail.budget_exhausted')}</Text>
            </Alert>
          )}

          <Stack gap={6}>
            <Text size="sm" fw={600}>
              {t('knowledge_loop.run_detail.units', { count: run.units.length })}
            </Text>
            {run.units.length === 0 ? (
              <Text size="xs" c="gray.5">
                {t('knowledge_loop.run_detail.no_units')}
              </Text>
            ) : (
              run.units.map((u) => (
                <Group
                  key={u.id}
                  gap={8}
                  wrap="nowrap"
                  style={{ border: CARD_BORDER, borderRadius: 8, padding: '6px 10px' }}
                >
                  <Badge size="xs" variant="light" color={proposalColor(u.proposal_type)}>
                    {t(`knowledge_loop.proposal.${u.proposal_type}`)}
                  </Badge>
                  <Text size="xs" lineClamp={1} style={{ flex: 1 }}>
                    {u.title}
                  </Text>
                  <Badge size="xs" variant="dot" color={unitStatusColor(u.status)}>
                    {t(`knowledge_loop.unit_status.${u.status}`)}
                  </Badge>
                </Group>
              ))
            )}
          </Stack>

          {run.status !== 'running' && (
            <Stack gap={6}>
              {redoConfirm ? (
                <Alert color="orange" variant="light">
                  <Stack gap={8}>
                    <Text size="xs">
                      {t('knowledge_loop.run_detail.redo_confirm', {
                        count: run.units.filter((u) => u.status === 'candidate').length,
                      })}
                    </Text>
                    <Group gap={8} justify="flex-end">
                      <Button size="xs" variant="default" onClick={() => setRedoConfirm(false)}>
                        {t('knowledge_loop.cancel')}
                      </Button>
                      <Button size="xs" color="orange" loading={redoing} onClick={redo}>
                        {t('knowledge_loop.run_detail.redo')}
                      </Button>
                    </Group>
                  </Stack>
                </Alert>
              ) : (
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconRotate size={14} />}
                    onClick={() => setRedoConfirm(true)}
                  >
                    {t('knowledge_loop.run_detail.redo')}
                  </Button>
                </Group>
              )}
            </Stack>
          )}

          {!!s.skip_samples?.length && (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                {t('knowledge_loop.run_detail.skip_samples')}
              </Text>
              {s.skip_samples.map((sk, i) => (
                <Group key={`${sk.source_ref}-${i}`} gap={8} wrap="nowrap" align="flex-start">
                  <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>
                    {sk.source_ref}
                  </Badge>
                  <Text size="xs" c="gray.7">
                    {sk.reason || '—'}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
};
